/**
 * HELPER PARA URLs LEGALES (TÉRMINOS Y PRIVACIDAD)
 * 
 * Genera URLs dinámicas para términos y condiciones y políticas de privacidad
 * según el entorno (desarrollo o producción) y la app.
 * 
 * @package @shared/navigation
 */

import { getAppOrigin } from './appSSOConfig';

/**
 * Detecta si estamos en modo desarrollo
 */
function isDevelopment(): boolean {
  // Durante el build (SSR), usar solo import.meta.env.DEV con fallback seguro
  if (typeof window === 'undefined') {
    return import.meta.env?.DEV ?? false;
  }
  // En runtime, verificar también hostname
  try {
    return (import.meta.env?.DEV ?? false) || 
           window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1';
  } catch {
    // Si hay error accediendo a window.location, usar solo env
    return import.meta.env?.DEV ?? false;
  }
}

/**
 * Obtiene la URL base de Accounts/Elfom según el entorno
 * Las páginas legales canónicas viven en accounts.elfom.com
 */
function getTheFomLabsBaseUrl(): string {
  const isDev = isDevelopment();
  return isDev ? 'http://localhost:5179' : 'https://accounts.elfom.com';
}

/**
 * Obtiene la URL de términos y condiciones para una app específica
 * 
 * @param appName - Nombre de la app (ej: 'empresa', 'houseme')
 * @returns URL completa de términos o null si la app no existe
 */
export function getTermsUrl(appName?: string | null): string {
  // Si es empresa, usar su propia URL de términos
  if (appName === 'empresa') {
    try {
      const appOrigin = getAppOrigin('empresa', isDevelopment());
      if (appOrigin) {
        return `${appOrigin}/terms`;
      }
    } catch (error) {
      // Si hay error durante build, usar URL de producción por defecto
      console.warn('Error obteniendo origin de empresa, usando TheFomLabs:', error);
    }
  }
  
  // Para otras apps o por defecto, usar TheFomLabs
  return `${getTheFomLabsBaseUrl()}/terms`;
}

/**
 * Obtiene la URL de política de privacidad
 * Siempre apunta a TheFomLabs (documento maestro del ecosistema)
 * 
 * @returns URL completa de política de privacidad
 */
export function getPrivacyUrl(): string {
  return `${getTheFomLabsBaseUrl()}/privacy`;
}

/**
 * Obtiene la URL del Acuerdo Maestro (términos unificados del ecosistema)
 * Siempre apunta a TheFomLabs
 * 
 * @returns URL completa del acuerdo maestro
 */
export function getMasterTermsUrl(): string {
  return `${getTheFomLabsBaseUrl()}/terms`;
}

/** Addendum legal del ecosistema (ruta canónica /legal-addendum). */
export function getLegalAddendumUrl(): string {
  return `${getTheFomLabsBaseUrl()}/legal-addendum`;
}

/** Alias corto /legal → mismo contenido que el addendum. */
export function getLegalUrl(): string {
  return `${getTheFomLabsBaseUrl()}/legal`;
}
